import { jsxs, jsx } from "react/jsx-runtime";
import { forwardRef, useRef, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { CheckboxWrapper, CheckboxDisplay, CheckboxInput, CheckboxContent, CheckboxTitle, CheckboxText } from "./checkbox.styles.es.js";
const propTypes = {
  checked: PropTypes.bool,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  disableHoverStyles: PropTypes.bool,
  indeterminate: PropTypes.bool,
  noWrap: PropTypes.bool,
  onChange: PropTypes.func,
  paddingX: PropTypes.oneOf(["medium", "none", "small", "xsmall"]),
  paddingY: PropTypes.oneOf(["medium", "none", "small", "xsmall"]),
  title: PropTypes.string,
  tone: PropTypes.oneOf(["critical", "neutral"])
};
const defaultProps = {
  checked: false,
  children: void 0,
  disabled: false,
  disableHoverStyles: false,
  indeterminate: false,
  noWrap: false,
  onChange: void 0,
  paddingX: "none",
  paddingY: "none",
  title: void 0,
  tone: "neutral"
};
const Checkbox = forwardRef(
  ({
    checked,
    children,
    disabled,
    disableHoverStyles,
    indeterminate,
    onChange,
    noWrap,
    paddingX,
    paddingY,
    title,
    tone,
    ...restProps
  }, ref) => {
    const clickedRef = useRef(false);
    const handleChange = useCallback(
      (event) => {
        clickedRef.current = true;
        if (onChange) {
          onChange(event);
        }
      },
      [onChange]
    );
    useEffect(() => {
      clickedRef.current = false;
    }, [checked, indeterminate]);
    return /* @__PURE__ */ jsxs(
      CheckboxWrapper,
      {
        $disabled: disabled,
        $noWrap: noWrap,
        $paddingX: paddingX,
        $paddingY: paddingY,
        children: [
          /* @__PURE__ */ jsx(
            CheckboxDisplay,
            {
              $animate: checked && !indeterminate && clickedRef.current,
              $checked: checked,
              $disabled: disabled,
              $disableHoverStyles: disableHoverStyles,
              $indeterminate: indeterminate,
              $tone: tone,
              children: /* @__PURE__ */ jsx(
                CheckboxInput,
                {
                  ref,
                  checked,
                  disabled,
                  onChange: handleChange,
                  ...restProps
                }
              )
            }
          ),
          (children || title) && /* @__PURE__ */ jsxs(CheckboxContent, { children: [
            title && /* @__PURE__ */ jsx(CheckboxTitle, { children: title }),
            children && /* @__PURE__ */ jsx(CheckboxText, { children })
          ] })
        ]
      }
    );
  }
);
Checkbox.propTypes = propTypes;
Checkbox.defaultProps = defaultProps;
export {
  Checkbox
};
